import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Locale } from "@/i18n";
import { localePath } from "@/lib/nav";
import { usePartnerModal } from "@/context/PartnerModalContext";
import { HERO_SLIDES, HERO_SIZES } from "./heroSlides";

const SLIDE_DURATION_MS = 5000;

export default function HeroSlider() {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language as Locale) || "it";
  const partner = usePartnerModal();
  const [current, setCurrent] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Trigger initial zoom transition on the first slide after mount.
    const raf = requestAnimationFrame(() => setMounted(true));
    const id = setInterval(() => {
      setCurrent((c) => (c + 1) % HERO_SLIDES.length);
    }, SLIDE_DURATION_MS);
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(id);
    };
  }, []);

  return (
    <section
      className="relative w-full h-[100svh] min-h-[640px] overflow-hidden bg-black"
    >
      {HERO_SLIDES.map(({ src, optimizedSrc, srcSet }, i) => {
        const next = (current + 1) % HERO_SLIDES.length;
        const isActive = i === current;
        const shouldRender = isActive || i === next;
        if (!shouldRender) return null;
        return (
        <div
          key={src}
          className="absolute inset-0 transition-opacity duration-[1400ms] ease-out"
          style={{
            opacity: isActive ? 1 : 0,
            transform: isActive && (i !== 0 || mounted)
              ? (i % 2 === 0 ? "scale(1.08) translateY(-1.2%)" : "scale(1.08) translateY(1%)")
              : "scale(1.0)",
            transition: "opacity 1400ms ease, transform 7000ms ease-out",
            willChange: "transform, opacity",
            contain: "paint",
            backfaceVisibility: "hidden",
          }}
          aria-hidden={!isActive}
        >
          <img
            src={optimizedSrc}
            srcSet={srcSet}
            sizes={HERO_SIZES}
            onError={(event) => {
              const image = event.currentTarget;
              if (image.getAttribute("src") === src) return;
              image.removeAttribute("srcset");
              image.src = src;
            }}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover"
            {...(i === 0
              ? { fetchPriority: "high" as const, loading: "eager" as const, decoding: "async" as const }
              : { fetchPriority: "low" as const, loading: "eager" as const, decoding: "async" as const })}
          />
        </div>
        );
      })}

      {/* Gradients */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/70 via-black/30 to-black/80" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/60 to-transparent" />
      <div className="lc-grain" />

      {/* Top eyebrow rail */}
      <div className="absolute top-24 inset-x-0 z-10">
        <div className="lc-container-ext flex items-center justify-between text-white/70 text-[11px] tracking-[0.3em] uppercase">
          <span>{t("hero.kicker.italian_craftsmanship")}</span>
          <span
            key={`cap-${current}`}
            className="lc-caption flex items-center gap-3"
            aria-live="polite"
          >
            <span className="hidden sm:inline-block h-px w-8 bg-bronze/70" />
            <span className="font-serif italic text-white text-[18px] md:text-[22px] leading-none normal-case tracking-normal drop-shadow-[0_1px_8px_rgba(0,0,0,0.55)]">
              {HERO_SLIDES[current]?.collection}
            </span>
          </span>
        </div>
      </div>

      {/* Hero copy */}
      <div className="relative z-10 h-full flex flex-col justify-end pb-28 md:pb-36">
        <div className="lc-container-ext">
          <div className="max-w-[1100px]">
            <h1 className="font-display text-white font-light leading-[0.95] tracking-[-0.045em] text-[56px] md:text-[96px] lg:text-[140px]">
              <span className="block lc-animate-fade-up">{t("hero.title_1")}</span>
              <span className="block italic font-serif font-normal text-white/95 lc-animate-fade-up lc-animate-delay-1">
                {t("hero.title_2")}
              </span>
            </h1>
            <p className="mt-8 max-w-xl text-white/80 text-[15px] md:text-[17px] leading-relaxed lc-animate-fade-up lc-animate-delay-2" style={{ textShadow: "0 1px 12px rgba(0,0,0,0.45)" }}>
              {t("home.section.luxury_details.subtitle")} — {t("about.hero.subtitle")}
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3 lc-animate-fade-up lc-animate-delay-2">
              <button
                type="button"
                onClick={partner.open}
                className="lc-btn bg-white/90 text-foreground border border-white/0 hover:bg-white"
              >
                {t("button.become_partner")}
                <ArrowUpRight className="h-4 w-4" />
              </button>
              <Link to={localePath(locale, "catalogues")} className="lc-btn border border-white/40 text-white bg-white/5 hover:bg-white hover:text-foreground">
                {t("button.discover_collections")}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Progress + counter */}
      <div className="absolute bottom-8 right-8 z-10 hidden md:flex flex-col items-end gap-3 w-[280px]">
        <div className="flex items-baseline gap-2 text-white/85 text-[11px] tracking-[0.3em]">
          <span className="text-white">{String(current + 1).padStart(2, "0")}</span>
          <span className="text-white/45">/ {String(HERO_SLIDES.length).padStart(2, "0")}</span>
        </div>
        <div className="w-full h-px bg-white/20 overflow-hidden">
          <div
            key={`prog-${current}`}
            className="lc-progress-bar h-full w-full bg-bronze"
          />
        </div>
        <div className="flex gap-1.5">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-[2px] transition-all ${i === current ? "w-10 bg-white" : "w-5 bg-white/40 hover:bg-white/70"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
